import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Spinner,
  Switch,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { BellIcon } from "@chakra-ui/icons";
import { fetchNotificationPrefs, saveNotificationPrefs } from "../../services/messagingService";
import {
  useMessagingNotifications,
  requestNotificationPermission,
  setDesktopEnabled,
  dismissPermissionPrompt,
} from "../../utils/messagingNotifications";

// The person's notification levels (docs/specs/messaging-notifications-spec.md): one for this
// computer's browsers, one for the iOS app. Per-channel overrides are in each channel's bell.
export const LEVELS = [
  { value: "all", label: "All messages" },
  { value: "mentions", label: "Mentions & DMs" },
  { value: "none", label: "Nothing" },
];

const PERMISSION_WORDS = {
  granted: "Allowed in this browser.",
  denied: "Blocked in this browser. Allow notifications for this site in the browser's settings to get them.",
  default: "Not turned on in this browser yet.",
  unsupported: "This browser can't show notifications.",
};

// The dismissible line at the top of the Messages window that asks for the browser's permission.
// The request only ever comes from its button: a click.
export function DesktopNotificationsPrompt() {
  const { permission, promptDismissed, userId } = useMessagingNotifications();
  if (!userId || permission !== "default" || promptDismissed) return null;
  return (
    <HStack px={4} py={2} bg="blue.50" borderBottom="1px solid" borderColor="blue.100" spacing={3} data-testid="notify-prompt">
      <BellIcon color="blue.600" />
      <Text fontSize="sm" color="blue.900" flex="1">
        Get a desktop notification when someone messages or mentions you.
      </Text>
      <Button size="xs" colorScheme="blue" onClick={() => requestNotificationPermission()}>
        Turn on desktop notifications
      </Button>
      <Button size="xs" variant="ghost" onClick={() => dismissPermissionPrompt()}>
        Not now
      </Button>
    </HStack>
  );
}

export default function NotificationSettings() {
  const toast = useToast();
  const { permission, desktopEnabled } = useMessagingNotifications();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPrefs(await fetchNotificationPrefs());
    } catch (err) {
      toast({ title: "Couldn't load notification settings", description: err.message, status: "error", duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const save = async (patch) => {
    const before = prefs;
    setPrefs((p) => ({ ...(p || {}), ...patch }));
    setSaving(true);
    try {
      setPrefs(await saveNotificationPrefs(patch));
    } catch (err) {
      // The server's own words: e.g. the 2026-11-03 migration hasn't been run.
      setPrefs(before);
      toast({ title: "Couldn't save notification settings", description: err.message, status: "error", duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const toggleDesktop = async (on) => {
    if (!on) {
      setDesktopEnabled(false);
      return;
    }
    setDesktopEnabled(true);
    if (permission === "default") await requestNotificationPermission();
  };

  return (
    <Popover placement="bottom-end" isLazy onOpen={load}>
      <PopoverTrigger>
        <IconButton aria-label="Notification settings" title="Notifications" icon={<BellIcon />} size="sm" variant="ghost" />
      </PopoverTrigger>
      <PopoverContent w="340px" fontWeight="normal" fontSize="sm">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="semibold">Notifications</PopoverHeader>
        <PopoverBody>
          <VStack align="stretch" spacing={4}>
            {loading && !prefs ? (
              <HStack>
                <Spinner size="sm" />
                <Text>Loading…</Text>
              </HStack>
            ) : (
              <>
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Desktop (this computer's browsers)</FormLabel>
                  <Select
                    size="sm"
                    aria-label="Desktop notifications"
                    value={prefs?.desktop_level || "mentions"}
                    isDisabled={saving || !prefs}
                    onChange={(e) => save({ desktop_level: e.target.value })}
                  >
                    {LEVELS.map((l) => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Mobile (the iOS app)</FormLabel>
                  <Select
                    size="sm"
                    aria-label="Mobile notifications"
                    value={prefs?.mobile_level || "mentions"}
                    isDisabled={saving || !prefs}
                    onChange={(e) => save({ mobile_level: e.target.value })}
                  >
                    {LEVELS.map((l) => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            <Box>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel htmlFor="notify-desktop-switch" fontSize="sm" mb={0}>
                  Desktop notifications in this browser
                </FormLabel>
                <Switch
                  id="notify-desktop-switch"
                  isChecked={permission === "granted" && desktopEnabled}
                  isDisabled={permission === "denied" || permission === "unsupported"}
                  onChange={(e) => toggleDesktop(e.target.checked)}
                />
              </FormControl>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {permission === "granted" && !desktopEnabled ? "Off in this browser." : PERMISSION_WORDS[permission] || ""}
              </Text>
            </Box>
            <Text fontSize="xs" color="gray.500">
              A channel's bell overrides these for that channel, or mutes it.
            </Text>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
